"use client"

import HomeDetailsWrapperAccordion from "@/features/homeowners/components/home-details-wrapper-accordion"
import { useWorkscopeLabelStyle, WORKSCOPE_LABEL_COLORS } from "../home-details-wrapper-accordion"
import FormSelectInput from "../../../../shared/components/form-inputs/form-select-input"
import WorkScopeStatusSelect from "../work-scope-status-select"
import FormMultiSelectInput from "../../../../shared/components/form-inputs/form-multi-select-input"
import FormTextInput from "@/shared/components/form-inputs/form-text-input"
import FormTextareaInput from "../../../../shared/components/form-inputs/form-textarea-input"
import DateStampedTextarea from "../../../../shared/components/form-inputs/form-date-stamped-textarea-input"
import FormYesNoInput from "../../../../shared/components/form-inputs/form-yes-no-input"
import { workScopeStatusOptions } from "../../constants/options"
import { HomeWithHomeOwnerFormType } from "../../schema/home-with-home-owner.schema"
import { useFormContext, useWatch } from "react-hook-form"
import { useClaimedByOptions } from "../../hooks/use-claimed-by-options"
import { useWorkscopeClaimedBySync } from "../../hooks/use-workscope-claimed-by-sync"
import { GoodwillToggleLabel } from "../goodwill-toggle-label"

const goodwillGreenStyle = { color: WORKSCOPE_LABEL_COLORS.goodwill } as const
const BASE_FIELD_NAME = "home.hvacInfo"


function HomeHVACForm() {
    const goodwillLabelStyle = useWorkscopeLabelStyle(true) ?? goodwillGreenStyle
    const { control, setValue, getValues } = useFormContext<HomeWithHomeOwnerFormType>()
    const { options: claimedByOptions, loading: claimedByLoading } = useClaimedByOptions()
    const goodwillHvacOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillHvacOn` })
    const goodwillMechanicalRoughInOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillMechanicalRoughInOn` })
    
    // Watch status fields and claimed by fields
    const hvac = useWatch({ control, name: `${BASE_FIELD_NAME}.hvac` })
    const hvacClaimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.claimedBy` })
    const mechanicalRoughIn = useWatch({ control, name: `${BASE_FIELD_NAME}.mechanicalRoughIn` })
    const mechanicalRoughInClaimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.mechanicalRoughInClaimedBy` })
    const goodwillHvac = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillHvac` })
    const goodwillHvacClaimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillHvacClaimedBy` })
    const goodwillMechanicalRoughIn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillMechanicalRoughIn` })
    const goodwillMechanicalRoughInClaimedBy = useWatch({
        control,
        name: `${BASE_FIELD_NAME}.goodwillMechanicalRoughInClaimedBy`,
    })

    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: hvac,
        claimedBy: hvacClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.claimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: mechanicalRoughIn,
        claimedBy: mechanicalRoughInClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.mechanicalRoughInClaimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillHvac,
        claimedBy: goodwillHvacClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillHvacClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillHvacOn,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillMechanicalRoughIn,
        claimedBy: goodwillMechanicalRoughInClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillMechanicalRoughInClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillMechanicalRoughInOn,
    })

    return (
        <HomeDetailsWrapperAccordion 
            heading="HVAC"
            wrapperClassName="xl:grid-cols-2"
        >
            <WorkScopeStatusSelect workScopeSlug="hvac-final"
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="HVAC" toggleName={`${BASE_FIELD_NAME}.goodwillHvacOn` as any} control={control} />,
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.hvac`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{
                    label: "Claimed By",
                }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.claimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillHvacOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{ label: "HVAC Goodwill", labelStyle: goodwillLabelStyle }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillHvac`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "HVAC Goodwill Claimed By" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillHvacClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            <WorkScopeStatusSelect workScopeSlug="rough-mechanical"
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="Mechanical Rough In" toggleName={`${BASE_FIELD_NAME}.goodwillMechanicalRoughInOn` as any} control={control} />,
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.mechanicalRoughIn`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{
                    label: "Mechanical Rough In Claimed By",
                }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.mechanicalRoughInClaimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillMechanicalRoughInOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{ label: "Mechanical Rough In Goodwill", labelStyle: goodwillLabelStyle }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillMechanicalRoughIn`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "Mechanical Rough In Goodwill Claimed By" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillMechanicalRoughInClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            <DateStampedTextarea
                includeUserName
                inputGroupProps={{
                    label: "Give a brief description of the HVAC work needed",
                    wrapperClassName: "xl:col-span-2"
                }}
                rows={4}
                name={`${BASE_FIELD_NAME}.description`}
                control={control}
            />
        </HomeDetailsWrapperAccordion>
    )
}

export default HomeHVACForm
